
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Typography, TextField

} from '@mui/material';

import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';

import { DataGrid } from '@mui/x-data-grid';

import config from '../../config/config';
import { getAuthToken } from '../../utils/token';

import {
    getWarehouses,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
} from '../../services/warehouses';

const initialForm = {
    product_id: null,
    production_date: new Date().toISOString().slice(0, 10),
    actual_quantity: '',
    shift: 'Day',
    notes: '',
};

const Warehouses = () => {
    const [rows, setRows] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);

    const [selectedProduct, setSelectedProduct] = useState(null);

    const [search, setSearch] = useState('');
    const [shiftFilter, setShiftFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const notify = useCallback((message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    }, []);

    // --------------------------------------------------
    // Load products for Autocomplete
    // --------------------------------------------------
    const fetchProducts = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            notify(
                'Authentication token not found. Please log in.',
                'error'
            );
            return;
        }

        setOptionsLoading(true);

        try {
            const response = await axios.get(
                `${config.API}/product/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                    },
                }
            );

            const products =
                response?.data?.products ??
                response?.data?.data ??
                [];

            setProductOptions(
                products.map((product) => ({
                    value: product.id,
                    label:
                        product.name ??
                        product.product_name ??
                        product.productName ??
                        `Product ${product.id}`,
                }))
            );
        } catch (error) {
            console.error('Error loading products:', error);

            notify(
                error.response?.data?.message ||
                'Unable to load product list.',
                'error'
            );
        } finally {
            setOptionsLoading(false);
        }
    }, [notify]);

    // --------------------------------------------------
    // Load warehouse records
    // --------------------------------------------------
    const fetchWarehouses = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            notify(
                'Authentication token not found. Please log in.',
                'error'
            );
            return;
        }

        setLoading(true);

        try {
            const params = {
                search: search.trim() || undefined,
                shift: shiftFilter || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                record_status: 1,
            };

            const response = await getWarehouses(params);

            const records = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.data?.data)
                    ? response.data.data
                    : Array.isArray(response?.data?.data?.data)
                        ? response.data.data.data
                        : [];

            setRows(
                records.map((record) => ({
                    ...record,
                    id: record.id,
                }))
            );
        } catch (error) {
            console.error('Error loading warehouses:', error);

            notify(
                error.response?.data?.message ||
                'Unable to load warehouse records.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    }, [search, shiftFilter, fromDate, toDate, notify]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        fetchWarehouses();
    }, [fetchWarehouses]);

    // --------------------------------------------------
    // Open Add modal
    // --------------------------------------------------
    const handleAdd = () => {
        setEditId(null);
        setForm({
            ...initialForm,
            production_date: new Date().toISOString().slice(0, 10),
        });
        setSelectedProduct(null);
        setOpen(true);
    };

    // --------------------------------------------------
    // Open Edit modal
    // --------------------------------------------------
    const handleEdit = (row) => {
        setEditId(row.id);

        const product =
            productOptions.find(
                (item) => Number(item.value) === Number(row.product_id)
            ) || {
                value: row.product_id,
                label: row.product_name || `Product ${row.product_id}`,
            };

        setSelectedProduct(product);

        setForm({
            product_id: row.product_id,
            production_date: row.production_date
                ? String(row.production_date).slice(0, 10)
                : '',
            actual_quantity: row.actual_quantity ?? '',
            shift: row.shift || 'Day',
            notes: row.notes || '',
        });

        setOpen(true);
    };

    // --------------------------------------------------
    // Handle form field changes
    // --------------------------------------------------
    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    // --------------------------------------------------
    // Save warehouse record
    // --------------------------------------------------
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.product_id) {
            notify('Please select a product.', 'error');
            return;
        }

        if (!form.production_date) {
            notify('Please select a production date.', 'error');
            return;
        }

        if (
            form.actual_quantity === '' ||
            !Number.isFinite(Number(form.actual_quantity)) ||
            Number(form.actual_quantity) <= 0
        ) {
            notify(
                'Actual quantity must be greater than zero.',
                'error'
            );
            return;
        }

        if (!['Day', 'Night'].includes(form.shift)) {
            notify('Please select Day or Night shift.', 'error');
            return;
        }

        const payload = {
            product_id: Number(form.product_id),
            production_date: form.production_date,
            actual_quantity: Number(form.actual_quantity),
            shift: form.shift,
            notes: form.notes.trim() || null,
        };

        setSaving(true);

        try {
            if (editId) {
                await updateWarehouse(editId, payload);

                notify(
                    'Warehouse record updated successfully.',
                    'success'
                );
            } else {
                await createWarehouse(payload);

                notify(
                    'Warehouse record created successfully.',
                    'success'
                );
            }

            setOpen(false);
            setEditId(null);
            setForm(initialForm);
            setSelectedProduct(null);

            await fetchWarehouses();
        } catch (error) {
            console.error('Error saving warehouse:', error);

            const validationErrors =
                error.response?.data?.errors;

            const firstValidationError = validationErrors
                ? Object.values(validationErrors).flat()[0]
                : null;

            notify(
                firstValidationError ||
                error.response?.data?.message ||
                'Unable to save warehouse record.',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    // --------------------------------------------------
    // Logical delete
    // --------------------------------------------------
    const handleDelete = async (id) => {
        if (
            !window.confirm(
                'Are you sure you want to delete this warehouse record?'
            )
        ) {
            return;
        }

        try {
            await deleteWarehouse(id);

            notify(
                'Warehouse record deleted successfully.',
                'success'
            );

            await fetchWarehouses();
        } catch (error) {
            console.error('Error deleting warehouse:', error);

            notify(
                error.response?.data?.message ||
                'Unable to delete warehouse record.',
                'error'
            );
        }
    };

    // --------------------------------------------------
    // DataGrid columns
    // --------------------------------------------------
    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            width: 50,
        },
        {
            field: 'product_code',
            headerName: 'ItemCode',
            width: 70,
        },
        {
            field: 'product_name',
            headerName: 'Item',
            minWidth: 150,
            flex: 1,
            valueGetter: (value, row) =>
                row.product_name ||
                row.product?.name ||
                productOptions.find(
                    (p) => Number(p.value) === Number(row.product_id)
                )?.label ||
                `Product ${row.product_id}`,
        },
        {
            field: 'production_date',
            headerName: 'Prd Date',
            width: 110,
            valueFormatter: (value) =>
                value ? String(value).slice(0, 10) : '',
        },
        {
            field: 'actual_quantity',
            headerName: 'ActualQty',
            width: 110,
            type: 'number',
            valueFormatter: (value) =>
                value == null ? '0' : Number(value).toLocaleString(
                    undefined,
                    { maximumFractionDigits: 2 }
                ),
        },
        {
            field: 'shift',
            headerName: 'Shift',
            width: 80,
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 80,
        },
        {
            field: 'notes',
            headerName: 'Notes',
            minWidth: 100,
            flex: 1,
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 110,
            valueFormatter: (value) =>
                value ? String(value).replace('T', ' ').slice(0, 16) : '',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(params.row)}
                        >
                            <EditOutlined fontSize="small" />
                        </IconButton>

                    </Tooltip>

                  <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(params.row.id)}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    return (
        <Box sx={{ p: { xs: 1, sm: 1 } }}>
            {/* Header */}
            <Paper
                elevation={1}
                sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: 3,
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            Warehouse Management
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => {
                                fetchProducts();
                                fetchWarehouses();
                            }}
                            disabled={loading || optionsLoading}
                        >
                            Refresh
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                        >
                            Add Warehouse
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Filters */}
            <Paper
                elevation={1}
                sx={{
                    p: 1,
                    mb: 1,
                    borderRadius: 3,
                }}
            >
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Product, quantity, notes..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                   <Grid item xs={12} sm={6} md={3}>
    <TextField
        select
        fullWidth
        size="small"
        label="Shift"
        value={shiftFilter}
        onChange={(e) => setShiftFilter(e.target.value)}
        sx={{
            minWidth: 180,
            '& .MuiInputBase-root': {
                minHeight: 42,
            },
        }}
    >
        <MenuItem value="">All Shifts</MenuItem>
        <MenuItem value="Day">Day</MenuItem>
        <MenuItem value="Night">Night</MenuItem>
    </TextField>
</Grid>

                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="From Date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            slotProps={{
                                inputLabel: { shrink: true },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="To Date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            slotProps={{
                                inputLabel: { shrink: true },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={1}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                setSearch('');
                                setShiftFilter('');
                                setFromDate('');
                                setToDate('');
                            }}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* DataGrid */}
            <Paper
                elevation={1}
                sx={{
                    height: 300,
                    width: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 10,
                            },
                        },
                    }}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#eaf1fb',
                            fontWeight: 700,
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 700,
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                />
            </Paper>

            {/* Add/Edit modal */}
            <Dialog
                open={open}
                onClose={() => {
                    if (!saving) setOpen(false);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 700,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {editId
                        ? 'Edit Warehouse Record'
                        : 'Add Warehouse Record'}

                    <IconButton
                        onClick={() => setOpen(false)}
                        disabled={saving}
                        size="small"
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent>
                        <Stack spacing={1} sx={{ pt: 1 }}>
                            <Autocomplete
                                options={productOptions}
                                value={selectedProduct}
                                loading={optionsLoading}
                                getOptionLabel={(option) => option.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                    Number(option.value) === Number(value.value)
                                }
                                onChange={(event, newValue) => {
                                    setSelectedProduct(newValue);

                                    setForm((previous) => ({
                                        ...previous,
                                        product_id: newValue ? newValue.value : null,
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Product"
                                        placeholder="Select product"
                                        size="small"
                                        required
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {optionsLoading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={18}
                                                            sx={{ mr: 1 }}
                                                        />
                                                    ) : null}
                                                    {params.InputProps?.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            <TextField
                                fullWidth
                                required
                                type="date"
                                name="production_date"
                                label="Production Date"
                                value={form.production_date}
                                onChange={handleChange}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />

                            <TextField
                                fullWidth
                                required
                                type="number"
                                name="actual_quantity"
                                label="Actual Quantity"
                                value={form.actual_quantity}
                                onChange={handleChange}
                                inputProps={{
                                    min: 0.01,
                                    step: 0.01,
                                }}
                            />

                            <TextField
                                select
                                fullWidth
                                required
                                name="shift"
                                label="Shift"
                                value={form.shift}
                                onChange={handleChange}
                            >
                                <MenuItem value="Day">Day</MenuItem>
                                <MenuItem value="Night">Night</MenuItem>
                            </TextField>

                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={4}
                                name="notes"
                                label="Notes"
                                value={form.notes}
                                onChange={handleChange}
                                placeholder="Enter optional notes"
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ p: 1, pt: 0 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setOpen(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving || optionsLoading}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <Add />
                                )
                            }
                        >
                            {saving
                                ? 'Saving...'
                                : editId
                                    ? 'Update'
                                    : 'Save'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() =>
                    setSnackbar((previous) => ({
                        ...previous,
                        open: false,
                    }))
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((previous) => ({
                            ...previous,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Warehouses;
